import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProgramClass } from '../../types';
import {
  createProgramClass,
  updateProgramClass,
  CreateProgramClassData,
} from '../../services/programClassService';

interface ClassFormProps {
  programId: string;
  programName: string;
  classToEdit?: ProgramClass | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ClassForm: React.FC<ClassFormProps> = ({
  programId,
  programName,
  classToEdit,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!classToEdit;

  const [formData, setFormData] = useState({
    class_name: '',
    max_students: 20,
    waiting_list: 5,
    registration_start_date: '',
    registration_start_time: '09:00',
    registration_end_date: '',
    registration_end_time: '18:00',
    course_start_date: '',
    course_end_date: '',
    course_days: [] as string[],
    course_start_time: '10:00',
    course_end_time: '12:00',
    total_sessions: 0,
    tuition: 0,
    material_fee: 0,
    payment_deadline: '',
    refund_deadline: '',
    instructor: '',
    institution: '동두천시 글로벌인재교육센터',
    location: '',
    contact_person: '',
    contact_phone: '',
    selection_method: 'first-come' as 'first-come' | 'lottery',
    status: 'upcoming' as 'upcoming' | 'recruiting' | 'ongoing' | 'completed',
    status_mode: 'auto' as 'auto' | 'manual',
    is_published: true,
    display_order: 0,
    remarks: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const daysOfWeek = ['월', '화', '수', '목', '금', '토', '일'];

  // 수정 모드일 때 데이터 로드
  useEffect(() => {
    if (classToEdit) {
      setFormData({
        class_name: classToEdit.class_name,
        max_students: classToEdit.max_students,
        waiting_list: classToEdit.waiting_list,
        registration_start_date: classToEdit.registration_start_date,
        registration_start_time: classToEdit.registration_start_time || '09:00',
        registration_end_date: classToEdit.registration_end_date,
        registration_end_time: classToEdit.registration_end_time || '18:00',
        course_start_date: classToEdit.course_start_date,
        course_end_date: classToEdit.course_end_date,
        course_days: classToEdit.course_days || [],
        course_start_time: classToEdit.course_start_time || '10:00',
        course_end_time: classToEdit.course_end_time || '12:00',
        total_sessions: classToEdit.total_sessions || 0,
        tuition: classToEdit.tuition,
        material_fee: classToEdit.material_fee,
        payment_deadline: classToEdit.payment_deadline || '',
        refund_deadline: classToEdit.refund_deadline || '',
        instructor: classToEdit.instructor || '',
        institution: classToEdit.institution || '동두천시 글로벌인재교육센터',
        location: classToEdit.location || '',
        contact_person: classToEdit.contact_person || '',
        contact_phone: classToEdit.contact_phone || '',
        selection_method: classToEdit.selection_method,
        status: classToEdit.status,
        status_mode: classToEdit.status_mode || 'auto',
        is_published: classToEdit.is_published,
        display_order: classToEdit.display_order || 0,
        remarks: classToEdit.remarks || '',
      });
    }
  }, [classToEdit]);

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      course_days: prev.course_days.includes(day)
        ? prev.course_days.filter((d) => d !== day)
        : [...prev.course_days, day],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    if (!formData.class_name.trim()) {
      setError('반 이름을 입력해주세요.');
      return;
    }

    if (!formData.registration_start_date || !formData.registration_end_date) {
      setError('신청 기간을 입력해주세요.');
      return;
    }

    if (!formData.course_start_date || !formData.course_end_date) {
      setError('교육 기간을 입력해주세요.');
      return;
    }

    setSaving(true);

    try {
      const classData: CreateProgramClassData = {
        program_id: programId,
        class_name: formData.class_name,
        max_students: formData.max_students,
        waiting_list: formData.waiting_list,
        registration_start_date: formData.registration_start_date,
        registration_start_time: formData.registration_start_time,
        registration_end_date: formData.registration_end_date,
        registration_end_time: formData.registration_end_time,
        course_start_date: formData.course_start_date,
        course_end_date: formData.course_end_date,
        course_days: formData.course_days.length > 0 ? formData.course_days : undefined,
        course_start_time: formData.course_start_time,
        course_end_time: formData.course_end_time,
        total_sessions: formData.total_sessions > 0 ? formData.total_sessions : undefined,
        tuition: formData.tuition,
        material_fee: formData.material_fee,
        payment_deadline: formData.payment_deadline || undefined,
        refund_deadline: formData.refund_deadline || undefined,
        instructor: formData.instructor || undefined,
        institution: formData.institution || undefined,
        location: formData.location || undefined,
        contact_person: formData.contact_person || undefined,
        contact_phone: formData.contact_phone || undefined,
        selection_method: formData.selection_method,
        status: formData.status,
        status_mode: formData.status_mode,
        is_published: formData.is_published,
        display_order: formData.display_order,
        remarks: formData.remarks || undefined,
      };

      if (isEditMode) {
        await updateProgramClass({
          id: classToEdit!.id,
          ...classData,
        });
      } else {
        await createProgramClass(classData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving class:', err);
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditMode ? '반 수정' : '반 추가'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">강의: {programName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* 반 기본 정보 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">반 기본 정보</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    반 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.class_name}
                    onChange={(e) =>
                      setFormData({ ...formData, class_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="예: A반, 월수 오전반, 기본반"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    정원 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.max_students}
                    onChange={(e) =>
                      setFormData({ ...formData, max_students: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    대기 인원
                  </label>
                  <input
                    type="number"
                    value={formData.waiting_list}
                    onChange={(e) =>
                      setFormData({ ...formData, waiting_list: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* 신청 기간 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">신청 기간</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.registration_start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_start_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작 시간
                  </label>
                  <input
                    type="time"
                    value={formData.registration_start_time}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_start_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    종료일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.registration_end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_end_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    종료 시간
                  </label>
                  <input
                    type="time"
                    value={formData.registration_end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_end_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* 교육 기간 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">교육 기간</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.course_start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, course_start_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    종료일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.course_end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, course_end_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    수업 요일
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {daysOfWeek.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleDayToggle(day)}
                        className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                          formData.course_days.includes(day)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수업 시작 시간
                  </label>
                  <input
                    type="time"
                    value={formData.course_start_time}
                    onChange={(e) =>
                      setFormData({ ...formData, course_start_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수업 종료 시간
                  </label>
                  <input
                    type="time"
                    value={formData.course_end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, course_end_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    총 회차
                  </label>
                  <input
                    type="number"
                    value={formData.total_sessions}
                    onChange={(e) =>
                      setFormData({ ...formData, total_sessions: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    placeholder="예: 24"
                  />
                </div>
              </div>
            </div>

            {/* 수강료 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">수강료</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수강료 (원)
                  </label>
                  <input
                    type="number"
                    value={formData.tuition}
                    onChange={(e) =>
                      setFormData({ ...formData, tuition: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    재료비 (원)
                  </label>
                  <input
                    type="number"
                    value={formData.material_fee}
                    onChange={(e) =>
                      setFormData({ ...formData, material_fee: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    납입 기한
                  </label>
                  <input
                    type="date"
                    value={formData.payment_deadline}
                    onChange={(e) =>
                      setFormData({ ...formData, payment_deadline: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    환불 기한
                  </label>
                  <input
                    type="date"
                    value={formData.refund_deadline}
                    onChange={(e) =>
                      setFormData({ ...formData, refund_deadline: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* 강사/장소 정보 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">강사/장소 정보</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    강사
                  </label>
                  <input
                    type="text"
                    value={formData.instructor}
                    onChange={(e) =>
                      setFormData({ ...formData, instructor: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="강사명"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    기관
                  </label>
                  <input
                    type="text"
                    value={formData.institution}
                    onChange={(e) =>
                      setFormData({ ...formData, institution: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    장소
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="예: 동두천시 글로벌인재교육센터 2층 강의실"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    담당자
                  </label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_person: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    연락처
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="010-0000-0000"
                  />
                </div>
              </div>
            </div>

            {/* 기타 설정 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">기타 설정</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    선택 방법
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="first-come"
                        checked={formData.selection_method === 'first-come'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selection_method: e.target.value as 'first-come' | 'lottery',
                          })
                        }
                        className="mr-2"
                      />
                      선착순
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="lottery"
                        checked={formData.selection_method === 'lottery'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selection_method: e.target.value as 'first-come' | 'lottery',
                          })
                        }
                        className="mr-2"
                      />
                      추첨
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    상태 관리
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="auto"
                        checked={formData.status_mode === 'auto'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            status_mode: e.target.value as 'auto' | 'manual',
                          })
                        }
                        className="mr-2"
                      />
                      자동 (날짜 기반)
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="manual"
                        checked={formData.status_mode === 'manual'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            status_mode: e.target.value as 'auto' | 'manual',
                          })
                        }
                        className="mr-2"
                      />
                      수동
                    </label>
                  </div>
                </div>

                {formData.status_mode === 'manual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as any,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="upcoming">모집예정</option>
                      <option value="recruiting">모집중</option>
                      <option value="ongoing">진행중</option>
                      <option value="completed">종료</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) =>
                        setFormData({ ...formData, is_published: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">공개</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    정렬 순서
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    숫자가 작을수록 먼저 표시됩니다
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비고
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) =>
                      setFormData({ ...formData, remarks: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="준비물, 특별 사항 등을 입력하세요"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </form>

        {/* 푸터 */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? '저장 중...' : isEditMode ? '수정' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassForm;
